import React from "react";
import { useEffect, useState } from "react";
import "./StateBills.css";
import BillList from "../../components/BillList/BillList";
import Loading from "../Loading";
import { Box, Stack, Typography, Select, Option, Input, IconButton } from "@mui/joy";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LoadMoreButton from "../../components/LoadMoreButton/LoadMoreButton";
import SEOHead from "../../components/SEOHead/SEOHead";
import seoConfig from "../../utils/seoConfig";
import { statesList } from "../../StateCodes";
import CapitolImg from "../../tim-mossholder-LseXGRuWthI-unsplash.jpg";

function StateBills() {
  const urlParams = new URLSearchParams(window.location.search);
  const initialState = urlParams.get("state") || "";

  const [bills, setBills] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedState, setSelectedState] = useState(initialState);
  const [searchParams, setSearchParams] = useState({ keyword: "" });

  const fetchStateBills = async (reset = false, keyword = searchParams.keyword) => {
    if (!selectedState || loading || (!hasMore && !reset)) return;
    setLoading(true);
    if (reset) setBills([]);

    try {
      const response = await fetch(
        "/api/state-bill?" +
          new URLSearchParams({
            state: selectedState,
            keyword,
            limit: 20,
            lastKey: reset ? null : JSON.stringify(lastKey),
          }).toString()
      );
      const data = await response.json();
      const { items, lastKey: newLastKey } = data;
      setBills((prev) => (reset ? items : [...prev, ...items]));
      setLastKey(newLastKey);
      setHasMore(!!newLastKey);
    } catch (err) {
      console.error("Error fetching state bills:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStateBills(true);
  };

  const clearSearch = () => {
    setSearchParams({ keyword: "" });
    fetchStateBills(true, "");
  };

  const handleLoadMore = () => fetchStateBills(false);

  const handleStateChange = (_, newValue) => {
    setSelectedState(newValue);
    setSearchParams({ keyword: "" });
    setBills([]);
    setLastKey(null);
    setHasMore(true);
  };

  useEffect(() => {
    document.title = "State Bills & Legislation | CivicEngage";
    if (selectedState) fetchStateBills(true);
  }, [selectedState]);

  return (
    <>
      <SEOHead
        title="State Bills & Legislation | CivicEngage"
        description="Track and explore state legislation across the United States. Stay informed about bills that directly impact your local community."
        keywords="state bills, state legislation, state laws, local government, state congress"
        path="/state-bills"
        structuredData={[seoConfig.getBreadcrumbs("/state-bills", "State Bills & Legislation")]}
      />

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          backgroundColor: "#003049",
          backgroundImage: `linear-gradient(rgba(0,48,73,0.8), rgba(0,48,73,0.8)), url(${CapitolImg})`,
          backgroundSize: "100% 100%, cover",
          backgroundPosition: "0 0, center top",
          backgroundAttachment: "scroll, fixed",
        }}
      >
        <Stack direction="column" sx={{ position: "relative", zIndex: 1 }}>

          {/* Hero */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: { xs: "1.25rem", md: "2rem" },
              pt: { xs: "2.5rem", md: "3rem" },
              pb: { xs: "2rem", md: "2.5rem" },
            }}
          >
            <Box sx={{ maxWidth: "1200px", width: "100%" }}>
              <Stack spacing={4}>
                <Stack spacing={2}>
                  <Typography
                    component="p"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#fdf0d5",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    50 States
                  </Typography>
                  <Typography
                    level="h1"
                    sx={{
                      color: "white",
                      fontSize: { xs: "2.25rem", md: "3.75rem" },
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    State Legislation
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: "1.125rem",
                      lineHeight: 1.65,
                      maxWidth: "640px",
                    }}
                  >
                    Track bills and legislation in your state. Stay informed about the issues that matter most to your local community.
                  </Typography>
                </Stack>

                {/* State selector */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                    padding: "24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: "200px",
                      fontSize: "18px",
                      lineHeight: 1.55,
                      color: "#32383e",
                    }}
                  >
                    Select a state to browse its current legislation.
                  </Typography>
                  <Select
                    placeholder="Select your state"
                    value={selectedState}
                    onChange={handleStateChange}
                    size="lg"
                    sx={{
                      minWidth: "240px",
                      flexShrink: 0,
                      borderRadius: "6px",
                      fontSize: "16px",
                      "--Select-focusedHighlight": "var(--ce-accent)",
                    }}
                  >
                    {Object.entries(statesList).map(([abbr, name]) => (
                      <Option key={abbr} value={abbr}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </Box>
              </Stack>
            </Box>
          </Box>

          {/* Search bar — mirrors Bills.js: calc(2rem + 10px) accounts for Grid's half-spacing margin */}
          {selectedState && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                px: { xs: "calc(1.25rem + 8px)", md: "calc(2rem + 10px)" },
                pt: { xs: "2rem", md: "2.5rem" },
              }}
            >
              <Box sx={{ maxWidth: "1200px", width: "100%" }}>
                <Box
                  component="form"
                  onSubmit={handleSearch}
                  sx={{ display: "flex", gap: 1.5, width: "100%" }}
                >
                  <Input
                    placeholder={`Search ${statesList[selectedState] || "state"} legislation...`}
                    startDecorator={<SearchIcon sx={{ color: "#9ca3af" }} />}
                    value={searchParams.keyword}
                    onChange={(e) => setSearchParams({ keyword: e.target.value })}
                    endDecorator={
                      searchParams.keyword ? (
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={clearSearch}
                          aria-label="Clear search"
                          sx={{ color: "#9ca3af" }}
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                    sx={{
                      flex: 1,
                      backgroundColor: "white",
                      border: "1px solid rgba(0,0,0,0.23)",
                      borderRadius: "4px",
                      fontSize: "16px",
                      "--Input-focusedHighlight": "var(--ce-accent)",
                      "&:focus-within": {
                        borderColor: "var(--ce-accent)",
                        boxShadow: "0 0 0 3px rgba(0,117,178,0.15)",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Bill list — renders at top level so its internal maxWidth aligns with search */}
          {loading && bills.length === 0 && selectedState ? (
            <Loading />
          ) : selectedState ? (
            <>
              <BillList
                bills={bills}
                loading={loading}
                hasMore={hasMore}
                searchParams={searchParams}
                onClearSearch={clearSearch}
                isStateBills={true}
              />
              <LoadMoreButton
                onLoadMore={handleLoadMore}
                loading={loading}
                hasMore={hasMore}
                currentCount={bills.length}
                itemType="state bills"
              />
            </>
          ) : (
            <Box sx={{ py: "40px", textAlign: "center" }}>
              <Typography sx={{ fontSize: "18px", color: "rgba(255,255,255,0.6)" }}>
                Select a state above to view its legislation.
              </Typography>
            </Box>
          )}

        </Stack>
      </Box>
    </>
  );
}

export default StateBills;
